import React, { useState, useRef, useEffect } from 'react';
import './FloatingDropdown.scss';

interface IDropdownOption {
  label: string;
  value: string | number;
}

interface IFloatingDropdownProps {
  label: string;
  name: string;
  value: string | number;
  options: IDropdownOption[];
  onChange: (option: IDropdownOption) => void;
  error?: string;
}

const FloatingDropdown: React.FC<IFloatingDropdownProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  error = '',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasValue = value !== '' && value !== null && value !== undefined;
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectClick = () => {
    setIsOpen(!isOpen);
    setIsFocused(true);
  };

  const handleOptionClick = (option: IDropdownOption) => {
    onChange(option);
    setIsOpen(false);
    setIsFocused(false);
  };

  const wrapperClasses = [
    'textbox',
    hasValue || isFocused ? 'isNotEmpty' : '',
    error ? 'error' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} ref={dropdownRef}>
        <div
          className="dropdown-select"
          onClick={handleSelectClick}
          tabIndex={0}
          onFocus={() => setIsFocused(true)}
        >
          <div className="dropdown-value">
            {displayValue}
          </div>
        </div>
        <label
          htmlFor={name}
          className="input-label"
        >
          {label}
        </label>
          <svg
            className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
            width="12"
            height="8"
            viewBox="0 0 12 8"
          >
            <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
          </svg>

        {isOpen && (
          <div className="dropdown-options">
            {options.map(option => (
              <div
                key={option.value}
                className={`dropdown-option ${option.value === value ? 'selected' : ''}`}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default FloatingDropdown;
